import type { HelpLanguage } from "./types";

/**
 * Shared look of the guide's surfaces: stacking order, the few fixed colours, and the chrome's
 * own microcopy. Kept apart from types.ts because that file must stay free of anything the
 * surfaces alone care about.
 */

/**
 * The guide renders ABOVE the app's dialogs, so it stays usable while the dialog it documents
 * is open. The base sits above the page-scroll indicator in AppContent (999999) and below the
 * react-toastify container (9999999), so app notifications still show on top.
 */
const MODAL_LAYER = 1_000_000;

export const Z = {
  /**
   * The launcher portals' mount node. Fluent's portal containers (dialogs included) all sit at
   * MODAL_LAYER with a stacking context of their own, so the values below only beat a dialog
   * mounted before them — this one beats it whichever came first.
   */
  portal: MODAL_LAYER + 1,
  launcherCopy: MODAL_LAYER + 1,
  floating: MODAL_LAYER + 1,
  drawer: MODAL_LAYER + 2,
  spotlight: MODAL_LAYER + 20,
  card: MODAL_LAYER + 21,
  banner: MODAL_LAYER + 22,
  jump: MODAL_LAYER + 25,
} as const;

/** Spotlight / hover outline — must read against both the light and the dark theme. */
export const ACCENT = "#f0a028";
/** The inspector banner and the floating button's dismiss badge. */
export const INK = "#0f172a";
export const SHADOW_CARD = "0 12px 32px rgba(15, 23, 42, 0.28)";
export const SHADOW_SOFT = "0 6px 18px rgba(15, 23, 42, 0.22)";

/** The drawer's width on anything wider than a phone. */
export const DRAWER_WIDTH = 440;

/**
 * The chrome's microcopy. EN is complete; another language may override any subset and the rest
 * falls back to EN — the same per-field rule the guides follow. Not in Phrase on purpose: the
 * guides themselves live in the repo, and their chrome ships with them.
 */
const EN = {
  launcherTooltip: (page: string) => `Page guide: ${page}`,
  launcherAria: (page: string) => `Open the page guide for ${page}`,
  floatingAria: "Open the page guide (floating shortcut)",
  floatingHide: "Hide this — the guide stays in the top bar",
  floatingHideAria: "Hide the floating page-guide button",
  floatingHidden:
    "Floating button hidden. The guide is still one click away: the “?” in the top bar.",
  undo: "Undo",
  eyebrow: "Page guide",
  close: "Close page guide",
  tour: "Show me around",
  inspect: "Point at a field",
  hint:
    "The tour visits what is on screen right now — or only the open dialog, when there is one; " +
    "its step counter jumps to any of them. “Point at a field” answers the other way round: " +
    "click the control you are looking at and read what it does.",
  searchAria: "Search this page’s guide",
  searchPlaceholder: (n: number) => `Search all ${n} entries…`,
  matchLine: (n: number, total: number, q: string) => `${n} of ${total} entries match “${q}”.`,
  noMatch: "Nothing on this page matches. The “Ask” tab searches the whole app.",
  goodToKnow: "Good to know",
  locate: "Show me where this is",
  locateAria: (title: string) => `Locate: ${title}`,
  startHere: "Walk me through from here",
  startHereAria: (title: string) => `Start the tour at: ${title}`,
  notOnScreen: "Not on screen right now.",
  floatingLabel: "Floating “?” button",
  floatingSwitchAria: "Show a floating page-guide button",
  floatingOn: "Shown in the bottom-right corner, where it can cover the chat button.",
  floatingOff:
    "Off — the “?” in the top bar opens this guide, and stays clickable while a dialog is open.",
  noticeDialogEmpty:
    "Nothing in this dialog is documented yet — close it to tour the page, or read the panel.",
  noticeNothing: "Nothing to point at on this screen yet — the guide in the panel covers it all.",
  noticeNotYet: (title: string, hint: string) => `“${title}” is not on screen yet. ${hint}`,
  noticeNotNow: (title: string) =>
    `“${title}” is not on screen right now, so the tour cannot open on it.`,
  stepCounter: (n: number, total: number) => `STEP ${n} OF ${total}`,
  stepCounterAria: (n: number, total: number) => `Step ${n} of ${total} — jump to another step`,
  tourAria: (n: number, total: number, title: string) => `Tour step ${n} of ${total}: ${title}`,
  prevSection: "Previous section",
  nextSection: "Next section",
  endTour: "End tour",
  back: "Back",
  next: "Next",
  done: "Done",
  jumpAria: "Search the steps on this page",
  jumpPlaceholder: "Search this page’s guide…",
  jumpEmpty:
    "Nothing on this screen matches. The panel lists the parts that are not on screen right now too.",
  jumpFooter: (n: number, total: number) => `${n} of ${total} · Enter goes to the first`,
  inspectBanner: "Click any field to read what it does",
  inspectEsc: "Esc to leave",
  inspectLeave: "Leave point-at-a-field mode",
  inspectMiss:
    "No guide entry for this control yet — try the field itself, or the page guide panel.",
  inspectCardAria: (title: string) => `What this does: ${title}`,
  continueHere: "Continue from here",
  // --- tabs and the "Ask" tab
  tabPage: "This page",
  tabAsk: "Ask",
  askPlaceholder: "Ask how to do something…",
  askHint: "Plain words work best — “enrol my child”, “cancel a package”, “change my address”.",
  askThinking: "Looking…",
  askEmpty: "Ask a question about any page of the app.",
  askTakeMeThere: "Take me there",
  askTakeMeThereOpen: "Take me there and open it",
  askShowMe: "Show me the control",
  askOpenIt: "Open it",
  askNeeds: "Needs",
  askInEnglish: "shown in English",
  askTryThese: "Try one of these:",
  askHelpful: "Did this help?",
  askThanks: "Thanks — noted.",
  askYes: "Answer helped",
  askNo: "Answer did not help",
  askOffPage: "This control is on another part of the page — open it from there first.",
  askMissNotOnScreen: "that control is not on screen right now.",
  askMissBehindSurface: "that control is inside what the highlighted button opens.",
  askChatbotHint:
    "For anything else — your cover, documents, a specific case — ask the AI assistant (the chat button).",
};

export type HelpUiText = typeof EN;

const OVERRIDES: Partial<Record<HelpLanguage, Partial<HelpUiText>>> = {};

export const uiText = (language: HelpLanguage): HelpUiText => ({
  ...EN,
  ...OVERRIDES[language],
});
